/* eslint-disable react/prop-types */
import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo
} from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  flexRender
} from '@tanstack/react-table';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { debounce } from 'lodash';
import { TableHeader } from './TableHeader';
import { TableBody } from './TableBody';
import { TableFooter } from './TableFooter';
import { useTableData } from './useTableData';
import { ColumnVisibilityModal } from './ColumnVisibilityModal';
import { getCommonPinningStyles, HeaderCell } from './HeaderCell';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import FalconCardHeader from 'components/common/FalconCardHeader';
import CardDropdown from 'components/common/CardDropdown';
import ExportButton from 'components/common/ExportButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import Pagin from 'components/doc-components/Pagination';
import { getTenantFromToken } from 'utils/routeUtils';
import { SERVICES } from 'api/common/constants';

const DEFAULT_PAGE_SIZE = 25;
const DEBOUNCE_DELAY = 500;
const DEFAULT_COLUMN_WIDTH = 400;

const Table = ({
  // ===== CENTRALIZED DATA PROPS =====
  centralizedTableData = [],
  centralizedTotalRows = 0,
  centralizedLoading = false,
  centralizedCanNextPage = false,
  disableInternalApiCalls = true,

  // ===== PROPS FROM PARENT WITH DEFAULTS =====
  tableData: propTableData = [],
  totalRows: propTotalRows = 0,
  pageSize: propPageSize = DEFAULT_PAGE_SIZE,
  pageIndex: propPageIndex = 0,
  canNextPage: propCanNextPage = false,
  isLoading: propIsLoading = false,
  globalFilters: propGlobalFilters = {
    search_text: '',
    start_date: '',
    end_date: ''
  },
  globalfilters: propGlobalfilters = {
    search_text: '',
    start_date: '',
    end_date: ''
  },
  handlePageChange: propHandlePageChange,
  handlePageSizeChange: propHandlePageSizeChange,
  handleGlobalSearch: propHandleGlobalSearch,
  handleDateChange: propHandleDateChange,

  // ===== CONFIGURATION PROPS =====
  apiPath = '',
  forWeather = false,
  district_ids = [],
  initialPageSize = DEFAULT_PAGE_SIZE,
  handleAction,
  dateRangePicker = false,
  buttons = [],
  title = '',
  handleDownload,
  showHeader = true,
  showFooter = true,
  errorMsg = '',
  dateFilter = false,
  exportKey = '',
  dropdownFilter = false,
  dropdownData = [],
  dropdownOptions = {},
  selectedValues = {},
  setSelectedValues,
  service = SERVICES.DIGITIZATION,
  statusField = 'is_active',
  idField = 'id',
  parentActionHandler,
  clearAllDates = () => {},
  currentFilters = {}
}) => {
  const tenant = getTenantFromToken();
  const navigate = useNavigate();

  // ===== USE CENTRALIZED DATA - NO INTERNAL API CALLS =====
  const tableData =
    centralizedTableData.length > 0 ? centralizedTableData : propTableData;
  const totalRows = centralizedTotalRows || propTotalRows;
  const loading = centralizedLoading || propIsLoading;
  const canNextPage = centralizedCanNextPage || propCanNextPage;
  const pageIndex = propPageIndex;
  const pageSize = propPageSize;

  // ===== CRITICAL: Use the correct globalfilters prop =====
  const globalFilters =
    propGlobalfilters.search_text !== undefined ||
    propGlobalfilters.start_date !== undefined ||
    propGlobalfilters.end_date !== undefined
      ? propGlobalfilters
      : propGlobalFilters;

  // ===== TABLE UI STATE =====
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [columnPinning, setColumnPinning] = useState({});
  const [columnVisibility, setColumnVisibility] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [columnFilters, setColumnFilters] = useState([]);

  // ===== COLUMN MANAGEMENT =====
  const { columns, fuzzyFilter } = useTableData(tableData);

  // ===== ENHANCED ACTION HANDLER =====
  const enhancedHandleAction = useCallback(
    (action, rowData) => {
      if (action === 'Activate' || action === 'Deactivate') {
        if (parentActionHandler) {
          const result = parentActionHandler(action, rowData);
          if (result?.handled) return;
        }
      }

      if (handleAction) {
        handleAction(action, rowData);
      }
    },
    [handleAction, parentActionHandler]
  );

  // ===== TABLE INSTANCE =====
  const table = useReactTable({
    data: tableData,
    columns,
    defaultColumn: {
      minSize: 60,
      size: DEFAULT_COLUMN_WIDTH
    },
    state: {
      columnFilters,
      showFilters,
      pagination: {
        pageIndex,
        pageSize
      },
      columnPinning,
      columnVisibility,
      rowSelection: {}
    },
    enableColumnFilters: true,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnPinningChange: setColumnPinning,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    filterFns: {
      fuzzy: fuzzyFilter
    },
    manualPagination: true,
    pageCount: Math.ceil(totalRows / pageSize)
  });

  // ===== EVENT HANDLERS =====
  const clearFilters = useCallback(() => {
    setColumnFilters([]);
  }, []);

  const toggleFilters = useCallback(() => {
    setShowFilters(prev => {
      if (prev) clearFilters();
      return !prev;
    });
  }, [clearFilters]);

  // ===== STABLE DEBOUNCED SEARCH WITH DEBUG LOGGING =====
  const debouncedSearch = useMemo(
    () =>
      debounce(input => {
        // Handle both string values and event objects
        const value =
          typeof input === 'string' ? input : input?.target?.value || '';
        if (
          propHandleGlobalSearch &&
          typeof propHandleGlobalSearch === 'function'
        ) {
          // Pass only the string value, not the event object
          propHandleGlobalSearch(value);
        }
      }, DEBOUNCE_DELAY),
    [propHandleGlobalSearch]
  );

  const filteredRowsCount = table.getFilteredRowModel().rows.length;

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="d-flex flex-column h-100 card">
        {showHeader && (
          <>
            <div>
              <FalconCardHeader
                title={title}
                titleTag="h5"
                light={true}
                endEl={
                  <div className="d-flex justify-content-end align-items-center my-auto gap-2">
                    <div className="d-flex align-items-center gap-2">
                      {buttons
                        ?.filter(button => button.enabled)
                        .map((button, index) => (
                          <Button
                            key={index}
                            variant="primary"
                            size="sm"
                            onClick={() => {
                              if (button.action) {
                                enhancedHandleAction(button.action);
                              } else if (button.redirectPath) {
                                navigate(`/${tenant}${button.redirectPath}`);
                              }
                            }}
                          >
                            {button.buttonName}
                          </Button>
                        ))}
                    </div>
                    {exportKey && (
                      <ExportButton
                        exportKey={exportKey}
                        currentFilters={currentFilters}
                      />
                    )}
                    <CardDropdown
                      showColumnVisiblity={setShowColumnSelector}
                      onExport={handleDownload}
                      toggleFilters={toggleFilters}
                      exportKey={exportKey}
                    />
                  </div>
                }
              />
            </div>
            <div className="flex-shrink-0 d-flex mt-3">
              <TableHeader
                table={table}
                setShowColumnSelector={setShowColumnSelector}
                showColumnSelector={showColumnSelector}
                handleGlobalSearch={debouncedSearch}
                dateRangePicker={dateRangePicker}
                globalfilters={globalFilters}
                handleDateChange={propHandleDateChange}
                dateFilter={dateFilter}
                dropdownFilter={dropdownFilter}
                dropdownData={dropdownData}
                dropdownOptions={dropdownOptions}
                selectedValues={selectedValues}
                setSelectedValues={setSelectedValues}
                clearAllDates={clearAllDates}
              />
            </div>
          </>
        )}

        <div className="flex-grow-1 overflow-auto position-relative">
          {loading && (
            <div
              className="position-absolute w-100 h-100 bg-white bg-opacity-75 d-flex justify-content-center align-items-center"
              style={{ zIndex: 1000 }}
            >
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}

          <table className="table table-bordered table-hover table-striped w-100">
            <thead
              className="bg-200 text-900 text-nowrap align-middle position-sticky"
              style={{
                zIndex: 3,
                backgroundColor: 'white',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                top: -1
              }}
            >
              {table.getHeaderGroups().map(headerGroup => (
                <tr
                  key={headerGroup.id}
                  className="align-middle white-space-nowrap"
                >
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      className="fw-semibold text-start border"
                      style={{
                        ...getCommonPinningStyles(
                          header.column,
                          table.getAllLeafColumns()
                        )
                      }}
                    >
                      {header.isPlaceholder ? null : (
                        <HeaderCell
                          header={header}
                          table={table}
                          showFilter={showFilters}
                        />
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <TableBody
              table={table}
              loading={loading}
              onAction={enhancedHandleAction}
              data={tableData}
              onPageSizeChange={propHandlePageSizeChange}
              globalfilters={globalFilters}
              errorMsg={errorMsg}
            />
          </table>
        </div>

        {showFooter && (
          <TableFooter
            table={table}
            totalRows={filteredRowsCount}
            onPageChange={propHandlePageChange}
            onPageSizeChange={propHandlePageSizeChange}
            canNextPage={canNextPage}
            canPreviousPage={pageIndex > 0}
            loading={loading}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        )}
      </div>

      <ColumnVisibilityModal
        isOpen={showColumnSelector}
        onClose={() => setShowColumnSelector(false)}
        table={table}
      />
    </DndProvider>
  );
};

export default Table;
